import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Badge, Heading, Skeleton, Text } from "@lendcord/concord";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { useGetPersonDetailQuery } from "./api/people.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { formatTimestampDate, formatWireDate } from "@/app/dateFormat";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const STATUS_BADGE: Record<string, "success" | "warning" | "info" | "error"> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "error",
};

function formatDateTime(iso: string) {
  const time = new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  return `${formatTimestampDate(iso)}, ${time}`;
}

interface PersonDetailViewProps {
  applicationId: string;
  onBack: () => void;
}

// Read-only — the full composed view PeopleDirectoryController's getDetail
// returns (profile + application + branch), unlike Onboarding's own
// ApplicationDetailView which only shows the application-submission fields.
// Sectioned the same way that view is, plus an Identity/KYC section built
// from `profile`.
export function PersonDetailView({ applicationId, onBack }: PersonDetailViewProps) {
  const intl = useIntl();
  const { data, error } = useGetPersonDetailQuery(applicationId);
  const loadError = error
    ? isQueryApiError(error)
      ? error.message
      : intl.formatMessage({ id: "lender.people.detailLoadError" })
    : undefined;

  const backLink = (
    <div className="mb-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={intl.formatMessage({ id: "lender.people.backToList" })}
        className="text-sm font-medium text-accent-600 hover:underline"
      >
        ‹ <FormattedMessage id="lender.people.backToList" />
      </button>
    </div>
  );

  if (loadError) {
    return (
      <div>
        {backLink}
        <AlertBanner status="error">{loadError}</AlertBanner>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        {backLink}
        <Skeleton variant="rect" className="h-64 w-full" />
      </div>
    );
  }

  const { profile, application, branchSelection } = data;
  const fullName = [profile.salutation, profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ");

  return (
    <div>
      {backLink}

      <div className="flex items-center gap-3">
        <Heading level={2}>{fullName}</Heading>
        <Badge status={STATUS_BADGE[application.status] ?? "info"}>
          <FormattedMessage id={`lender.onboarding.status${application.status}`} />
        </Badge>
      </div>
      <Text as="p" size="sm" variant="muted" className="mt-1">
        <FormattedMessage id="lender.onboarding.generatedReadableId" values={{ id: application.readableId }} />
      </Text>

      <div className="mt-6 rounded-md border border-border bg-surface-raised">
        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.people.identityHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ProfileField labelId="lender.profile.emailLabel" value={profile.email} />
            <ProfileField labelId="lender.profile.phoneLabel" value={profile.phone} />
            <ProfileField
              labelId="lender.onboarding.columnApplicationType"
              value={intl.formatMessage({
                id: application.applicationType === "BORROWER" ? "lender.onboarding.applicationTypeBorrower" : "lender.onboarding.applicationTypeLender",
              })}
            />
            <ProfileField labelId="lender.profile.panLabel" value={profile.pan ?? "–"} />
            <ProfileField labelId="lender.profile.aadhaarLabel" value={profile.aadhaar ?? "–"} />
            <ProfileField labelId="lender.profile.dateOfBirthLabel" value={profile.dob ? formatWireDate(profile.dob) : "–"} />
          </div>
        </section>

        {profile.addresses.length > 0 && (
          <section className="px-6 py-3">
            <Heading level={4} className="text-accent-600">
              <FormattedMessage id="lender.profile.addressesHeading" />
            </Heading>
            <div className="mt-4 flex flex-col gap-3">
              {profile.addresses.map((address) => (
                <Text as="p" size="sm" key={address.id}>
                  {address.line1}
                  {address.line2 ? `, ${address.line2}` : ""}, {address.city}, {address.state} {address.pincode}
                </Text>
              ))}
            </div>
          </section>
        )}

        {application.applicationType === "BORROWER" && (
          <section className="px-6 py-3">
            <Heading level={4} className="text-accent-600">
              <FormattedMessage id="lender.onboarding.loanDetailsHeading" />
            </Heading>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
              <ProfileField
                labelId="lender.onboarding.columnLoanAmount"
                value={application.loanAmount != null ? currencyFormatter.format(application.loanAmount) : "–"}
              />
            </div>
          </section>
        )}

        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.people.branchHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ProfileField labelId="lender.people.branchLabel" value={branchSelection.defaultBranch?.branchName ?? "–"} />
          </div>
        </section>

        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.onboarding.locationHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <ProfileField labelId="lender.onboarding.columnLocation" value={application.locationAddress ?? "–"} />
            </div>
            <ProfileField labelId="lender.onboarding.latitudeLabel" value={application.locationLatitude != null ? String(application.locationLatitude) : "–"} />
            <ProfileField labelId="lender.onboarding.longitudeLabel" value={application.locationLongitude != null ? String(application.locationLongitude) : "–"} />
          </div>
        </section>

        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.onboarding.additionalDetailsHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ProfileField labelId="lender.onboarding.centerNameLabel" value={application.centerName ?? "–"} />
            <ProfileField labelId="lender.onboarding.areaLabel" value={application.area ?? "–"} />
            <div className="sm:col-span-3">
              <ProfileField labelId="lender.onboarding.columnRemarks" value={application.remarks ?? "–"} />
            </div>
          </div>
        </section>

        <div className="px-6 pb-6">
          <Text size="xs" variant="muted">
            <FormattedMessage id="lender.onboarding.createdAtMeta" values={{ date: formatDateTime(application.createdAt) }} />
          </Text>
        </div>
      </div>
    </div>
  );
}
